import { supabase } from './supabase';

// Contact info helpers. The customer's records over the chat are in lib/records.ts. WhatsApp's own profile
// photo, About and status are not available through Meta's Cloud API, so nothing here pretends to have them.

/** "+27 83 629 3343" for a South African number; anything else as stored. */
export function formatPhone(e164: string | undefined): string | null {
  if (!e164) return null;
  const m = /^\+27(\d{2})(\d{3})(\d{4})$/.exec(e164);
  return m ? `+27 ${m[1]} ${m[2]} ${m[3]}` : e164;
}

/** "R 2 708,75", the way invoices show money. */
export function formatRand(amount: number): string {
  const [whole, frac] = (Math.round(amount * 100) / 100).toFixed(2).split('.');
  return `R ${(whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${frac ?? '00'}`;
}

/** What an operator needs beside the chat (Gary, 18 Oct), from messaging-send's context action. */
export interface ChatContext {
  leo: { handover: string | null; notes: string[]; quote: { total: number; lines: { item: string; quantity: number; line_total: number }[]; minimum: boolean } | null };
  booking: { booking_code: string; status: string; service_date: string; service_time: string | null; service_name: string | null; suburb: string | null } | null;
  service: { date: string; beds: number | null; items: { item: string; quantity: number }[]; last12: number; level: 'Bronze' | 'Silver' | 'Gold' | null } | null;
  /** How long the customer waited for a person, last 30 days, newest first (Gary, 20 Oct). */
  replies?: ReplyWait[];
  /** Peace of Sleep chats (3 Nov 2026): the POS client with its properties, and the POS lead. */
  pos?: PosContext;
}

export interface PosContext {
  client: { id: string; name: string; properties: { id: string; name: string; town: string | null; status: string | null }[] } | null;
  lead: { id: string; property: string; region: string | null; rooms: number | null; status: string; proposal: string | null } | null;
}

/** Add to POS Leads (Gary, 3 Nov 2026): the property the chat is about, typed by the POS team. */
export async function addPosLead(conversationId: string, propertyName: string): Promise<void> {
  const { error } = await supabase.functions.invoke('messaging-send', { body: { action: 'pos_add_lead', conversation_id: conversationId, property_name: propertyName } });
  if (error) {
    let message = 'That did not save. Try again.';
    try {
      const body = await (error as { context?: Response }).context?.json() as { error?: string } | undefined;
      if (body?.error) message = body.error;
    } catch { /* not JSON */ }
    throw new Error(message);
  }
}

/** One wait: working minutes from the customer's message to a person's reply, or until now while still waiting. */
export interface ReplyWait { at: string; minutes: number; by: string | null; covered: boolean; waiting: boolean }

export async function loadChatContext(conversationId: string): Promise<ChatContext | null> {
  const { data, error } = await supabase.functions.invoke('messaging-send', { body: { action: 'context', conversation_id: conversationId } });
  return error || !data ? null : (data as ChatContext);
}

/** The Portal for this country, where the full customer or lead record lives. */
/**
 * Opens a Sentinel (Portal) page already signed in (Gary, 15 Oct): Sentinel issues a one-time
 * sign-in link for your own login (messaging-send portal_link). The tab opens at once so the
 * phone does not block it, then goes to the link; if that fails it goes to the page itself.
 */
export async function openInSentinel(path: string): Promise<void> {
  // One named tab for Sentinel, so switching back and forth reuses it (Gary, 18 Oct).
  const tab = window.open('', SENTINEL_WINDOW);
  const { data, error } = await supabase.functions.invoke('messaging-send', { body: { action: 'portal_link', path } });
  const url = !error && (data as { url?: string } | null)?.url ? (data as { url: string }).url : portalUrl(path);
  if (tab) tab.location.href = url;
  else window.location.href = url;
}

export const SENTINEL_WINDOW = 'cmb-sentinel';

/** The Sentinel page for a chat: its customer, its lead, or Sentinel's home. */
export function sentinelPathFor(c: { customerId?: string; leadId?: string } | null | undefined): string {
  // The customer's card in Sentinel's customer list, as people use it (Gary, 18 Oct).
  if (c?.customerId) return `/customers?view=${c.customerId}`;
  if (c?.leadId) return `/leads?id=${c.leadId}`;
  return '/';
}

export function portalUrl(path: string): string {
  const cc = (import.meta.env.VITE_COUNTRY_CODE ?? 'za').toLowerCase();
  return `https://${cc}.app.cleanmybed.com${path}`;
}

// ---------------------------------------------------------------- Customer card (Gary, 18 Oct)
// The most useful parts of the Sentinel customer record, for the Customer tab beside the chat. Read only
// and under the person's own access (row rules); changes are made in Sentinel.

